import { useSyncExternalStore } from "react";

/**
 * A session-scoped "this principal is denied" latch, shared by every read of one surface.
 *
 * Several surfaces gate on a single backend role and are read by more than one hook at a time (a list, a
 * detail pane, a trail). A missing permission surfaces as a **401** — the RBAC guard throws Unauthorized, not
 * Forbidden — and every 401 costs a silent `/auth/refresh` + replay inside `HttpApiService`. So one denial
 * must silence the whole surface for the rest of the session, instead of each hook rediscovering it on every
 * query change.
 *
 * It is a **store, not a plain `Set`**, and that is the point. A `Set` at module scope mutates without
 * telling React, so any hook that copied it into `useState` keeps a stale flag: its requests stop (the
 * fetch-time guard still reads the live `Set`) while its `hasAccess` stays `true`, and a loading state that
 * resolves on a settled request then never resolves — an endless skeleton instead of the no-access view.
 * Reading through {@link AccessLatch.useDenied} removes the copy, so the flag cannot drift from the latch:
 *
 * - Any consumer latching re-renders **all** of them, which is what "one denial silences the surface" means.
 * - `getSnapshot` re-reads with the CURRENT principal key on every render, so a key that changes after mount
 *   (auth hydration resolving `user.id`, an organization arriving with a later `/auth/me`) is picked up
 *   instead of being frozen at whatever the first render saw.
 *
 * The key is the caller's to define: whatever identifies the principal whose access was denied. Include every
 * value the request is scoped by — an id alone is wrong when the same user reads two organizations.
 *
 * ```ts
 * const paymentsAccessLatch = createAccessLatch();
 * // in a hook:
 * const hasAccess = !paymentsAccessLatch.useDenied(deniedKey);
 * ```
 */
export interface AccessLatch {
  /** Non-reactive read, for the guard at the top of a fetcher. */
  isDenied: (principalKey: string) => boolean;
  /** Latch a principal. Idempotent: re-latching an already-denied key notifies nobody. */
  latch: (principalKey: string) => void;
  /** Reactive read. Re-renders on a latch AND when `principalKey` itself changes. */
  useDenied: (principalKey: string) => boolean;
}

/**
 * Builds one latch. Each call returns an independent store, so two surfaces never share denials even if a
 * principal key collides.
 */
export function createAccessLatch(): AccessLatch {
  const deniedPrincipals = new Set<string>();
  const listeners = new Set<() => void>();

  const subscribe = (listener: () => void): (() => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  };

  const isDenied = (principalKey: string): boolean => deniedPrincipals.has(principalKey);

  const latch = (principalKey: string): void => {
    // Notifying without a state change would re-render every consumer on each retried 401.
    if (deniedPrincipals.has(principalKey)) return;
    deniedPrincipals.add(principalKey);
    for (const listener of listeners) listener();
  };

  const useDenied = (principalKey: string): boolean =>
    // `subscribe` is stable and the snapshot is a primitive, so this cannot tear or loop.
    useSyncExternalStore(
      subscribe,
      () => deniedPrincipals.has(principalKey),
      // No SSR in this app; the server snapshot only has to be a stable non-denied default.
      () => false,
    );

  return { isDenied, latch, useDenied };
}
